export const VALIDATION_PATTERNS: Record<string, RegExp> = {
  geneSymbol: /^(?!rs)[A-Z][A-Za-z0-9-]{0,19}$/,
  ensemblId: /^ENS(?:[A-Z]{3})?G\d{11}$/i,
  uniprotId: /^(?:[OPQ][0-9][A-Z0-9]{3}[0-9]|[A-NR-Z][0-9]([A-Z][A-Z0-9]{2}[0-9]){1,2})$/i,
  geneId: /^\d+$/,
  strID: /^rs\d+$/i,
};

/**
 * Friendly names for identifier types
 */
const IDENTIFIER_NAMES = {
  geneSymbol: 'Gene Symbol',
  ensemblId: 'Ensembl ID',
  uniprotId: 'UniProt ID',
  geneId: 'NCBI Gene ID',
  strID: 'RS Number',
};

export const validateIdentifier = (identifier, type) => {
  if (!identifier || !identifier.trim()) return false;

  const pattern = VALIDATION_PATTERNS[type];
  if (!pattern) return false;

  return pattern.test(identifier.trim());
};

export const validateIdentifierList = (text, expectedType) => {
  if (!text || !text.trim()) {
    return {
      isValid: false,
      totalItems: 0,
      validItems: 0,
      invalidItems: [],
      errors: ['Input is empty'],
    };
  }

  const items = text
    .split('\n')
    .map(item => item.trim())
    .filter(item => item.length > 0);

  const invalidItems: Array<{ line: number; value: string; reason: string }> = [];
  let validCount = 0;

  items.forEach((item, index) => {
    if (validateIdentifier(item, expectedType)) {
      validCount++;
    } else {
      invalidItems.push({
        line: index + 1,
        value: item,
        reason: getValidationError(item, expectedType),
      });
    }
  });

  return {
    isValid: invalidItems.length === 0,
    totalItems: items.length,
    validItems: validCount,
    invalidItems,
    errors:
      invalidItems.length > 0
        ? [`${invalidItems.length} invalid ${IDENTIFIER_NAMES[expectedType]} identifier(s) found`]
        : [],
  };
};

const getValidationError = (identifier, expectedType) => {
  const typeName = IDENTIFIER_NAMES[expectedType];

  switch (expectedType) {
    case 'geneSymbol':
      return `"${identifier}" is not a valid gene symbol. Expected format: letters, numbers, and hyphens (e.g., BRCA1, TP53)`;
    case 'ensemblId':
      return `"${identifier}" is not a valid Ensembl ID. Expected format: ENS[species]G followed by 11 digits (e.g., ENSG00000139618)`;
    case 'uniprotId':
      return `"${identifier}" is not a valid UniProt ID. Expected format: 6-character alphanumeric code (e.g., P04637)`;
    case 'geneId':
      return `"${identifier}" is not a valid NCBI Gene ID. Expected format: numeric only (e.g., 7157)`;
    case 'strID':
      return `"${identifier}" is not a valid RS number. Expected format: rs followed by numbers (e.g., rs123456789)`;
    default:
      return `"${identifier}" is not a valid ${typeName}`;
  }
};

export const detectIdentifierType = (text: string): string | null => {
  if (!text || !text.trim()) return null;

  const items = text
    .split('\n')
    .map(s => s.trim())
    .filter(Boolean)
    .slice(0, 10);

  const priority = ['ensemblId', 'uniprotId', 'strID', 'geneId', 'geneSymbol'];

  const counts: Record<string, number> = {};

  items.forEach(item => {
    for (const type of priority) {
      const pattern = VALIDATION_PATTERNS[type];
      if (pattern && pattern.test(item)) {
        counts[type] = (counts[type] || 0) + 1;
        break;
      }
    }
  });

  let bestType: string | null = null;
  let bestCount = 0;
  for (const [type, cnt] of Object.entries(counts)) {
    if (cnt > bestCount) {
      bestType = type;
      bestCount = cnt;
    }
  }

  return bestType && bestCount >= Math.ceil(items.length * 0.5) ? bestType : null;
};

export const validateTypeConsistency = (text, selectedType, searchType) => {
  const detectedType = detectIdentifierType(text);

  if (!detectedType) {
    return {
      isConsistent: false,
      message: 'Unable to detect identifier type from input',
      detectedType: null,
    };
  }

  if (searchType === 'VARIANTS') {
    const isConsistent = detectedType === 'strID';
    return {
      isConsistent,
      message: isConsistent
        ? 'Input matches expected variant identifier format'
        : `Input appears to contain ${IDENTIFIER_NAMES[detectedType]} but variant analysis requires RS numbers`,
      detectedType,
    };
  }

  if (searchType === 'GENES') {
    const isConsistent = selectedType === detectedType;
    return {
      isConsistent,
      message: isConsistent
        ? `Input matches selected ${IDENTIFIER_NAMES[selectedType]} format`
        : `Input appears to contain ${IDENTIFIER_NAMES[detectedType]} but ${IDENTIFIER_NAMES[selectedType]} is selected`,
      detectedType,
    };
  }

  return {
    isConsistent: true,
    message: 'Validation passed',
    detectedType,
  };
};
